import { useTranslation } from 'react-i18next';
import { FieldEditReasonWithStaff, FormChangesDto, FormChangingActionType } from 'src/services/api/api-client';

export const getActorName = (actionType: FormChangingActionType, version: FormChangesDto): string | undefined => {
  switch (actionType) {
    case FormChangingActionType.Approve:
      return version.approvedBy?.fullName;
    case FormChangingActionType.Recall:
      return version.approvalIsRecalledBy?.fullName;
    case FormChangingActionType.Submit:
      return version.submittedBy?.fullName;
    case FormChangingActionType.ChangeFields:
      return version.updatedBy?.fullName;
    case FormChangingActionType.Skipped:
      return version.submittedBy?.fullName;
    case FormChangingActionType.ApprovedAfterSkipping:
      return version.approvedBy?.fullName;
    case FormChangingActionType.SubmissionRevoked:
      return version.submissionRevokedBy?.fullName;
  }
};

export const resolveChangingTime = (currentVersion: FormChangesDto) => {
  switch (currentVersion.formChangingActionType) {
    case FormChangingActionType.Approve:
      return currentVersion.approvedAt;
    case FormChangingActionType.ChangeFields:
      return currentVersion.updatedAt;
    case FormChangingActionType.ApprovedAfterSkipping:
      return currentVersion.approvedAt;
    case FormChangingActionType.Recall:
      return currentVersion.approvalIsRecalledAt;
    case FormChangingActionType.Skipped:
      return currentVersion.updatedAt;
    case FormChangingActionType.SubmissionRevoked:
      return currentVersion.submissionRevokedAt;
    case FormChangingActionType.Submit:
      return currentVersion.submittedAt;
  }
};

export const useGetFormUpdatingActionsName = (actionType: FormChangingActionType): string | undefined => {
  const { t } = useTranslation();

  switch (actionType) {
    case FormChangingActionType.Approve:
      return t('Forms.HistoryOfChanges.FormUpdatingActions.Approve');
    case FormChangingActionType.Recall:
      return t('Forms.HistoryOfChanges.FormUpdatingActions.Recall');
    case FormChangingActionType.Submit:
      return t('Forms.HistoryOfChanges.FormUpdatingActions.Submit');
    case FormChangingActionType.ChangeFields:
      return t('Forms.HistoryOfChanges.FormUpdatingActions.ChangeFields');
    case FormChangingActionType.Skipped:
      return t('Forms.HistoryOfChanges.FormUpdatingActions.Skipped');
    case FormChangingActionType.ApprovedAfterSkipping:
      return t('Forms.HistoryOfChanges.FormUpdatingActions.ApprovedAfterSkipping');
    case FormChangingActionType.SubmissionRevoked:
      return t('Forms.HistoryOfChanges.FormUpdatingActions.SubmissionRevoked');
  }
};

export const hasSameEditReason = (
  editReasons: { [key: string]: FieldEditReasonWithStaff },
  reasonOfChange: keyof FieldEditReasonWithStaff,
): boolean => {
  if (Object.keys(editReasons).length === 0) return false;

  const arrayOfEditReasons = Object.values(editReasons).map((er) => er[reasonOfChange]);
  const hasDuplicatedReasons = arrayOfEditReasons.every((el) => el === arrayOfEditReasons[0]);

  return hasDuplicatedReasons;
};

export const getPreviousVersionOfSelectedVersion = (
  formHistory?: FormChangesDto[],
  selectedVersion?: FormChangesDto,
) => {
  if (!selectedVersion || !formHistory) return undefined;

  const isZeroMinorVersion = selectedVersion.minorVersionNumber === 0;

  return formHistory.find(
    (v) =>
      (isZeroMinorVersion
        ? v.versionNumber === selectedVersion?.versionNumber - 1
        : v.minorVersionNumber === selectedVersion?.minorVersionNumber - 1 &&
          v.versionNumber === selectedVersion?.versionNumber) &&
      v.formChangingActionType === FormChangingActionType.ChangeFields,
  );
};

export const FormVersionFilterExpression = (version?: FormChangesDto): boolean => {
  if (!version) return false;

  const initialFormCondition =
    version.formChangingActionType === FormChangingActionType.ChangeFields &&
    Object.keys(version.fieldEditReasons).length > 0;

  const formActionsRecordCondition =
    Object.keys(version.fieldEditReasons).length >= 0 &&
    version.formChangingActionType !== FormChangingActionType.ChangeFields;

  return initialFormCondition || formActionsRecordCondition;
};
